import React, {
   useEffect,
   useState,
   useCallback,
   useLayoutEffect,
} from 'react';
import { FiDownload, FiInfo } from 'react-icons/fi';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { ModalContent } from '../components/ModalContent';
import { RequireAccessLevel } from '../components/RequireAccessLevel';
import { Title } from '../components/Title';
import { ShowSiteLoading } from '../SiteLoadingIndicator';
import { ContentScreen } from '../styles/ContentScreen';
import { accessLevels, assetsUrl, authStatuses } from '../util/constants';
import { getSubscriptionsFromState } from '../util/getSubscriptionsFromState';
import { Table } from './Table';

const DownloadsStyles = styled(ContentScreen)`
   @media (max-width: 600px) {
      .exercise-actions-cell {
         display: none;
      }

      td {
         line-height: 1.3;
         padding-bottom: 1em;
      }
   }

   article {
      margin-bottom: 3em;
   }
`;

const canAccess = (access, authenticated, activeSubscriptions) =>
   access === accessLevels.unauthenticated ||
   (access === accessLevels.authenticated && authenticated) ||
   (access === accessLevels.exercise && activeSubscriptions.exercise) ||
   (access === accessLevels.course && activeSubscriptions.course);

const mapStateToProps = (state) => ({
   activeSubscriptions: getSubscriptionsFromState(state),
   authenticated: state.appData.userData.status === authStatuses.authenticated,
});

const Downloads = connect(mapStateToProps)(
   ({ activeSubscriptions, authenticated }) => {
      const [downloads, setDownloads] = useState(null);
      const [requireAccess, setRequireAccess] = useState(null);
      const [showModal, setShowModal] = useState(false);
      const [requestedDownloadId, setRequestedDownloadId] = useState(null);

      useEffect(() => {
         fetch(
            `${assetsUrl}/${
               activeSubscriptions.exercise ? 'secure' : 'public'
            }/exercise/downloads/downloadsList.json`,
            {
               credentials: 'include',
            }
         )
            .then((res) => res.json())
            .then(setDownloads);
      }, [activeSubscriptions.exercise]);

      const { search } = useLocation();

      const selectedId = new URLSearchParams(search).get('selected');

      useLayoutEffect(() => {
         window.scrollTo({ top: 0 });
      }, [selectedId]);

      const selectedDownload = downloads
         ? downloads.find(({ id }) => id === selectedId)
         : null;

      const requestedDownload = downloads
         ? downloads.find(({ id }) => id === requestedDownloadId)
         : null;

      const createCantDownloadHandler = useCallback(
         ({ access, id }) =>
            () => {
               setRequireAccess(access);
               setShowModal(true);
               setRequestedDownloadId(id);
            },
         []
      );

      return (
         <>
            <DownloadsStyles className="narrow">
               <Title title="Downloads" />
               <h1>Downloads</h1>

               {downloads == null && <ShowSiteLoading />}

               {selectedDownload && (
                  <article>
                     <h2>{selectedDownload.title}</h2>
                     <p>{selectedDownload.description}</p>
                     <div>
                        {canAccess(
                           selectedDownload.access,
                           authenticated,
                           activeSubscriptions
                        ) ? (
                           <Button
                              as="a"
                              href={selectedDownload.link}
                              target="_blank"
                              rel="noopener"
                              className="button-cta"
                           >
                              Download
                           </Button>
                        ) : (
                           <Button
                              onClick={createCantDownloadHandler(
                                 selectedDownload
                              )}
                              className="button-cta"
                           >
                              Download
                           </Button>
                        )}
                     </div>
                  </article>
               )}

               <h2>All Downloads</h2>

               <Table className="row-hover">
                  <tbody>
                     {downloads &&
                        downloads.map(({ title, id, link, access }) => (
                           <tr key={id}>
                              <td>
                                 <Link to={`?selected=${id}`}>{title} </Link>
                              </td>
                              <td className="exercise-actions-cell">
                                 <div
                                    style={{ display: 'flex', height: '1.5em' }}
                                 >
                                    <Link to={`?selected=${id}`}>
                                       <FiInfo size="1.5em" />
                                    </Link>
                                    {canAccess(
                                       access,
                                       authenticated,
                                       activeSubscriptions
                                    ) ? (
                                       // eslint-disable-next-line react/jsx-no-target-blank
                                       <a
                                          href={link}
                                          target="_blank"
                                          rel="noopener"
                                       >
                                          <FiDownload size="1.5em" />
                                       </a>
                                    ) : (
                                       <Button
                                          className="button-link"
                                          onClick={createCantDownloadHandler({
                                             id,
                                             access,
                                          })}
                                       >
                                          <FiDownload size="1.5em" />
                                       </Button>
                                    )}
                                 </div>
                              </td>
                           </tr>
                        ))}
                  </tbody>
               </Table>
            </DownloadsStyles>
            <Modal display={showModal}>
               {requireAccess ? (
                  <RequireAccessLevel
                     requiredAccessLevel={requireAccess}
                     resourceName="download"
                     onCancel={() => {
                        setShowModal(false);
                     }}
                     onAccessAllowed={() => setRequireAccess(null)}
                  />
               ) : (
                  requestedDownload && (
                     <ModalContent>
                        <h1>{requestedDownload.title}</h1>
                        <p className="modal-p">
                           {requestedDownload.description}
                        </p>
                        <div className="modal-button-wrapper">
                           <Button
                              as="a"
                              href={requestedDownload.link}
                              target="_blank"
                              rel="noopener"
                              className="button-cta"
                              onClick={() => setShowModal(false)}
                           >
                              Download
                           </Button>
                        </div>
                     </ModalContent>
                  )
               )}
            </Modal>
         </>
      );
   }
);

export { Downloads };
